import React, { useState } from 'react';
import { 
  AlertTriangle, FileText, Clock, User, Calendar, 
  CheckCircle, XCircle, Eye, Edit3, Plus, Search,
  AlertCircle, Shield, TrendingUp, TrendingDown,
  Download, Printer, Send, Flag, Bell, Users,
  Camera, Paperclip, MapPin, Activity, Zap
} from 'lucide-react';

const IncidentReporting = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showNewIncident, setShowNewIncident] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Mock incident data
  const incidentData = [
    {
      id: 'INC-001',
      title: 'Fall in Room A101',
      resident: 'Alice Smith',
      room: 'A101',
      date: '2024-07-20',
      time: '14:30',
      severity: 'High',
      status: 'Under Investigation',
      category: 'Fall',
      description: 'Resident fell while attempting to get out of bed. No visible injuries but complained of hip pain.',
      reporter: 'Nurse Jane Smith',
      witnesses: ['Dr. John Doe', 'Care Assistant Mike'],
      actions: ['Immediate assessment', 'X-ray ordered', 'Pain management'],
      followUp: 'Orthopedic consultation scheduled',
      escalation: 'Notified family and doctor',
      attachments: ['incident_photo_1.jpg', 'medical_report.pdf'],
      created: '2024-07-20 14:35',
      updated: '2024-07-20 16:45'
    },
    {
      id: 'INC-002',
      title: 'Medication Error',
      resident: 'Bob Johnson',
      room: 'A102',
      date: '2024-07-19',
      time: '09:15',
      severity: 'Medium',
      status: 'Resolved',
      category: 'Medication',
      description: 'Wrong medication administered - gave Metformin instead of Metoprolol. Error caught within 15 minutes.',
      reporter: 'Nurse Emily White',
      witnesses: ['Pharmacist Sarah'],
      actions: ['Immediate monitoring', 'Correct medication given', 'Protocol review'],
      followUp: 'Staff training scheduled',
      escalation: 'Pharmacy notified',
      attachments: ['medication_chart.pdf'],
      created: '2024-07-19 09:20',
      updated: '2024-07-19 10:30'
    },
    {
      id: 'INC-003',
      title: 'Equipment Malfunction',
      resident: 'Charlie Brown',
      room: 'B201',
      date: '2024-07-18',
      time: '11:00',
      severity: 'Low',
      status: 'Resolved',
      category: 'Equipment',
      description: 'Patient lift malfunctioned during transfer. No injury occurred, equipment replaced.',
      reporter: 'Care Assistant Lisa',
      witnesses: ['Nurse Tom'],
      actions: ['Equipment removed from service', 'Alternative lift used', 'Maintenance notified'],
      followUp: 'Equipment inspection completed',
      escalation: 'Maintenance team notified',
      attachments: ['equipment_report.pdf'],
      created: '2024-07-18 11:05',
      updated: '2024-07-18 12:00'
    },
    {
      id: 'INC-004',
      title: 'Aggressive Behavior',
      resident: 'Diana Prince',
      room: 'B202',
      date: '2024-07-17',
      time: '16:45',
      severity: 'High',
      status: 'Under Investigation',
      category: 'Behavioral',
      description: 'Resident became aggressive during personal care, struck care assistant.',
      reporter: 'Care Assistant Mark',
      witnesses: ['Nurse Sarah', 'Security Guard'],
      actions: ['De-escalation techniques used', 'Security called', 'Resident calmed'],
      followUp: 'Behavioral assessment scheduled',
      escalation: 'Family and doctor notified',
      attachments: ['incident_report.pdf', 'security_log.pdf'],
      created: '2024-07-17 16:50',
      updated: '2024-07-17 18:30'
    },
    {
      id: 'INC-005',
      title: 'Pressure Ulcer Development',
      resident: 'Eve Adams',
      room: 'C301',
      date: '2024-07-16',
      time: '08:30',
      severity: 'Medium',
      status: 'Monitoring',
      category: 'Medical',
      description: 'Stage 2 pressure ulcer discovered on sacrum during routine skin check.',
      reporter: 'Nurse Jennifer',
      witnesses: ['Wound Care Specialist'],
      actions: ['Wound care initiated', 'Pressure relief measures', 'Nutrition assessment'],
      followUp: 'Daily wound monitoring',
      escalation: 'Wound care team notified',
      attachments: ['wound_photo.jpg', 'care_plan.pdf'],
      created: '2024-07-16 08:35',
      updated: '2024-07-20 09:00'
    }
  ];

  const filteredIncidents = incidentData.filter(incident => {
    const matchesSearch = incident.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         incident.resident.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         incident.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSeverity = filterSeverity === 'all' || incident.severity === filterSeverity;
    const matchesStatus = filterStatus === 'all' || incident.status === filterStatus;
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const showNotification = (message, type) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-orange-600 bg-orange-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Under Investigation': return 'text-blue-600 bg-blue-100';
      case 'Resolved': return 'text-green-600 bg-green-100';
      case 'Monitoring': return 'text-yellow-600 bg-yellow-100';
      case 'Escalated': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleNewIncident = () => {
    setShowNewIncident(true);
    showNotification('Creating new incident report...', 'info');
  };

  const handleViewIncident = (incident) => {
    setSelectedIncident(incident);
    showNotification(`Viewing incident ${incident.id}`, 'success');
  };

  const handleEditIncident = (incident) => {
    console.log('Editing incident:', incident.id);
    showNotification(`Editing incident ${incident.id}`, 'info');
  };

  const handleEscalateIncident = (incident) => {
    console.log('Escalating incident:', incident.id);
    showNotification(`Incident ${incident.id} escalated to management`, 'success');
  };

  const handleResolveIncident = (incident) => {
    console.log('Resolving incident:', incident.id);
    showNotification(`Incident ${incident.id} marked as resolved`, 'success');
  };

  const handleDeleteIncident = (incident) => {
    console.log('Deleting incident:', incident.id);
    showNotification(`Incident ${incident.id} deleted`, 'success');
  };

  const handleExportIncidents = () => {
    console.log('Exporting incidents...');
    showNotification('Incident report exported successfully', 'success');
  };

  const handlePrintIncident = (incident) => {
    console.log('Printing incident:', incident.id);
    showNotification(`Printing incident ${incident.id}`, 'info');
  };

  const handleSendNotification = (incident) => {
    console.log('Sending notification for incident:', incident.id);
    showNotification(`Notification sent for incident ${incident.id}`, 'success');
  };

  const stats = {
    total: incidentData.length,
    high: incidentData.filter(i => i.severity === 'High').length,
    medium: incidentData.filter(i => i.severity === 'Medium').length,
    low: incidentData.filter(i => i.severity === 'Low').length,
    underInvestigation: incidentData.filter(i => i.status === 'Under Investigation').length,
    resolved: incidentData.filter(i => i.status === 'Resolved').length,
    monitoring: incidentData.filter(i => i.status === 'Monitoring').length
  };

  if (!onClose) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-[100]">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-4 mb-2 rounded-lg shadow-md text-white flex items-center gap-3 ${
                notification.type === 'success' ? 'bg-green-500' :
                notification.type === 'error' ? 'bg-red-500' : 'bg-blue-500'
              }`}
            >
              {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {notification.type === 'error' && <XCircle className="w-5 h-5" />}
              {notification.type === 'info' && <FileText className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-red-600" />
            Incident Reporting & Management
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'incidents', label: 'All Incidents', icon: FileText },
            { id: 'new', label: 'New Report', icon: Plus },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'bg-red-600 text-white'
                    : 'bg-red-100 text-red-700 hover:bg-red-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Incidents</p>
                    <h3 className="text-2xl font-bold text-gray-900">{stats.total}</h3>
                  </div>
                  <FileText className="w-8 h-8 text-blue-400" />
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">High Severity</p>
                    <h3 className="text-2xl font-bold text-red-600">{stats.high}</h3>
                  </div>
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Under Investigation</p>
                    <h3 className="text-2xl font-bold text-blue-600">{stats.underInvestigation}</h3>
                  </div>
                  <Clock className="w-8 h-8 text-blue-400" />
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Resolved</p>
                    <h3 className="text-2xl font-bold text-green-600">{stats.resolved}</h3>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
            </div>

            {/* Recent Incidents */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Recent Incidents</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {incidentData.slice(0, 5).map((incident) => (
                    <div key={incident.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${
                          incident.severity === 'High' ? 'bg-red-500' :
                          incident.severity === 'Medium' ? 'bg-orange-500' : 'bg-green-500'
                        }`}></div>
                        <div>
                          <h4 className="font-medium text-gray-900">{incident.title}</h4>
                          <p className="text-sm text-gray-600">{incident.resident} - {incident.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSeverityColor(incident.severity)}`}>
                          {incident.severity}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(incident.status)}`}>
                          {incident.status}
                        </span>
                        <button
                          onClick={() => handleViewIncident(incident)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incidents Tab */}
        {activeTab === 'incidents' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search incidents..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="flex gap-4">
                  <select
                    value={filterSeverity}
                    onChange={(e) => setFilterSeverity(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="all">All Severities</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="Under Investigation">Under Investigation</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Monitoring">Monitoring</option>
                    <option value="Escalated">Escalated</option>
                  </select>
                  <button
                    onClick={handleExportIncidents}
                    className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>
            </div>

            {/* Incidents List */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Incident</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resident</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reporter</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredIncidents.map((incident) => (
                      <tr key={incident.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {incident.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {incident.resident} ({incident.room})
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {incident.date} {incident.time}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSeverityColor(incident.severity)}`}>
                            {incident.severity}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(incident.status)}`}>
                            {incident.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {incident.reporter}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewIncident(incident)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditIncident(incident)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit Incident"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEscalateIncident(incident)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Escalate"
                            >
                              <Flag className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResolveIncident(incident)}
                              className="text-green-600 hover:text-green-900"
                              title="Resolve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handlePrintIncident(incident)}
                              className="text-gray-600 hover:text-gray-900"
                              title="Print"
                            >
                              <Printer className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* New Report Tab */}
        {activeTab === 'new' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Create New Incident Report</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Incident Title</label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Brief description of the incident"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resident</label>
                  <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500">
                    <option value="">Select resident...</option>
                    <option value="alice">Alice Smith (A101)</option>
                    <option value="bob">Bob Johnson (A102)</option>
                    <option value="charlie">Charlie Brown (B201)</option>
                    <option value="diana">Diana Prince (B202)</option>
                    <option value="eve">Eve Adams (C301)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500">
                    <option value="">Select category...</option>
                    <option value="fall">Fall</option>
                    <option value="medication">Medication</option>
                    <option value="equipment">Equipment</option>
                    <option value="behavioral">Behavioral</option>
                    <option value="medical">Medical</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                  <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500">
                    <option value="">Select severity...</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Detailed description of what happened..."
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Immediate Actions Taken</label>
                  <textarea
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="What immediate actions were taken to address the incident..."
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Witnesses</label>
                  <input
                    type="text"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="List witnesses (comma separated)"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                  <div className="flex items-center gap-4">
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      <Camera className="w-4 h-4" />
                      Add Photo
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                      <Paperclip className="w-4 h-4" />
                      Add Document
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setActiveTab('incidents')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    showNotification('Incident report created successfully', 'success');
                    setActiveTab('incidents');
                  }}
                  className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Create Report
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Incidents by Severity</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">High</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-red-500 h-2 rounded-full" style={{width: `${(stats.high / stats.total) * 100}%`}}></div>
                      </div>
                      <span className="text-sm font-medium">{stats.high}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Medium</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-orange-500 h-2 rounded-full" style={{width: `${(stats.medium / stats.total) * 100}%`}}></div>
                      </div>
                      <span className="text-sm font-medium">{stats.medium}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Low</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: `${(stats.low / stats.total) * 100}%`}}></div>
                      </div>
                      <span className="text-sm font-medium">{stats.low}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Resolution Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Resolved</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{width: `${(stats.resolved / stats.total) * 100}%`}}></div>
                      </div>
                      <span className="text-sm font-medium">{stats.resolved}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Under Investigation</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{width: `${(stats.underInvestigation / stats.total) * 100}%`}}></div>
                      </div>
                      <span className="text-sm font-medium">{stats.underInvestigation}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Monitoring</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div className="bg-yellow-500 h-2 rounded-full" style={{width: `${(stats.monitoring / stats.total) * 100}%`}}></div>
                      </div>
                      <span className="text-sm font-medium">{stats.monitoring}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Incident Detail Modal */}
        {selectedIncident && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <div className="bg-white rounded-2xl p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Incident Details: {selectedIncident.id}</h3>
                <button
                  onClick={() => setSelectedIncident(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Basic Information</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Title:</strong> {selectedIncident.title}</p>
                      <p><strong>Resident:</strong> {selectedIncident.resident} ({selectedIncident.room})</p>
                      <p><strong>Date/Time:</strong> {selectedIncident.date} at {selectedIncident.time}</p>
                      <p><strong>Category:</strong> {selectedIncident.category}</p>
                      <p><strong>Reporter:</strong> {selectedIncident.reporter}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Status & Severity</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Severity:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getSeverityColor(selectedIncident.severity)}`}>
                          {selectedIncident.severity}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Status:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedIncident.status)}`}>
                          {selectedIncident.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedIncident.description}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Witnesses</h4>
                  <ul className="text-sm text-gray-700">
                    {selectedIncident.witnesses.map((witness, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {witness}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Actions Taken</h4>
                  <ul className="text-sm text-gray-700">
                    {selectedIncident.actions.map((action, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Follow-up</h4>
                  <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded-lg">{selectedIncident.followUp}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Escalation</h4>
                  <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded-lg">{selectedIncident.escalation}</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Attachments</h4>
                  <div className="flex gap-2">
                    {selectedIncident.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                        <Paperclip className="w-4 h-4" />
                        <span className="text-sm">{attachment}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setSelectedIncident(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => handleEditIncident(selectedIncident)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Edit Incident
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentReporting;
